/* eslint-disable @next/next/no-img-element */
import { Link, Box } from '@packages/shared';
import { buildLinkWithArguments } from '@packages/cms-components';
import type { CmsImageType } from '@packages/cms-components/src/components/CmsImage/types';
import { BannerItemImage } from './BannerItemImage';

export type BannerItemType = {
  data: CmsImageType;
};

/**
 * Component that displays a banner image.
 * If a link is provided in the data, the image acts as a clickable link.
 *
 * @component
 * @param {Object} props - Properties passed to the component.
 * @param {CmsImageType} props.data - Data object containing banner details.
 * @returns {JSX.Element} A rendered banner item.
 *
 * @example
 * // Usage example:
 * <BannerItem data={someCmsImageData} />
 */
export const BannerItem = ({ data }: BannerItemType) => {
  const {
    field_link: link,
    field_link_target: linkTarget,
    field_tracking: linkTracking,
    field_link_anchor: linkAnchor,
    field_link_highlight: linkHighlight,
    field_link_searchfilter: linkSearchFilter,
    field_bonus_number: bonusNumber,
  } = data;

  const sxStyle = {
    display: 'block',
    marginBottom: 2,
    '& img': {
      display: 'block',
      height: 'auto',
      maxWidth: '100%',
    },
  };

  if (link && link !== '') {
    return (
      <Link
        sx={sxStyle}
        href={buildLinkWithArguments(link, {
          field_tracking: linkTracking || undefined,
          field_link_anchor: linkAnchor || undefined,
          field_link_highlight: linkHighlight || undefined,
          field_link_searchfilter: linkSearchFilter || undefined,
          field_bonus_number: bonusNumber || undefined,
        })}
        openInNewTab={!!linkTarget}
      >
        <BannerItemImage data={data} />
      </Link>
    );
  }

  return (
    <Box sx={sxStyle}>
      <BannerItemImage data={data} />
    </Box>
  );
};
